import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { Divider, Radio } from 'semantic-ui-react'
import CustomModal from 'src/components/configuracion/components/customModal'
import { NominaProcessStatus } from 'src/utils/enum'
import { formatMoney } from 'src/utils/formatter'
import { getPendingNominaAmount } from 'src/utils/nominaAmounts'
import '../../../../documentostributarios/modalSettleConflictNomina.scss'
import './ModalNominaImputation.styles.scss'

/**
 * Imputation choices the user can pick when matching a payment against
 * a document that has active nomina selections.
 */
export const NominaLinkMode = {
  EXISTING_NOMINA: 'EXISTING_NOMINA',
  NEW_NOMINA: 'NEW_NOMINA',
}

export const NominaImputationMode = NominaLinkMode

const IMPUTABLE_STATUSES = [
  NominaProcessStatus.SELECTED,
  NominaProcessStatus.IN_CREATED_NOMINA,
  NominaProcessStatus.IN_INFORMED_NOMINA,
  NominaProcessStatus.IN_BANK_NOMINA,
  NominaProcessStatus.APPROVED,
]

const statusLabels = {
  [NominaProcessStatus.SELECTED]: 'Seleccionada',
  [NominaProcessStatus.IN_CREATED_NOMINA]: 'Creada',
  [NominaProcessStatus.IN_INFORMED_NOMINA]: 'Informada',
  [NominaProcessStatus.IN_BANK_NOMINA]: 'En banco',
  [NominaProcessStatus.APPROVED]: 'Aprobada',
}

/**
 * Returns the active selections of a document/transaction nomina status
 * that still have pending amount and can therefore receive a new
 * imputation from this match.
 */
export const getImputableSelections = (nominaStatus) => {
  const rawSelections = nominaStatus?.inNominaProcess
  const selections = Array.isArray(rawSelections)
    ? rawSelections
    : rawSelections && typeof rawSelections === 'object'
      ? Object.values(rawSelections)
      : []

  return selections.filter(
    (sel) =>
      IMPUTABLE_STATUSES.includes(sel?.status) &&
      getPendingNominaAmount(sel) > 0,
  )
}

/**
 * Modal that asks the user to explicitly decide whether the match they
 * are about to create corresponds to an already approved/in-process
 * nomina selection or to a brand-new payment unrelated to existing
 * nominas. Replaces the implicit "settle conflict" alert.
 */
const ModalNominaImputation = ({
  open,
  onClose,
  onConfirm,
  document,
  conflictAmount,
}) => {
  const selections = useMemo(
    () => getImputableSelections(document?.nominaStatus || document?.meta?.nominaStatus),
    [document],
  )
  const [mode, setMode] = useState(NominaLinkMode.NEW_NOMINA)
  const [selectedSelectionId, setSelectedSelectionId] = useState(null)

  useEffect(() => {
    if (!open) return
    setMode(NominaLinkMode.NEW_NOMINA)
    setSelectedSelectionId(null)
  }, [open, document?.id])

  const handleConfirm = () => {
    if (mode === NominaLinkMode.EXISTING_NOMINA) {
      const sel = selections.find((s) => s.id === selectedSelectionId)
      if (!sel) return
      onConfirm({
        imputationMode: NominaLinkMode.EXISTING_NOMINA,
        nominaId: sel.nominaId,
        selectionId: sel.id,
      })
    } else {
      onConfirm({ imputationMode: NominaLinkMode.NEW_NOMINA })
    }
  }

  const isExistingDisabled = selections.length === 0
  const isConfirmDisabled =
    mode === NominaLinkMode.EXISTING_NOMINA && !selectedSelectionId

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title="Vincular pago a nómina"
      classNameModal="nomina-imputation-modal"
      classNameContent="nomina-imputation-modal-content-wrapper"
      classNameTitle="nomina-imputation-modal-title"
      size="tiny"
      confirmLabel="Aceptar"
      onConfirm={handleConfirm}
      confirmDisabled={isConfirmDisabled}
      cancelarLabel="Cancelar"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
      modalStyle={{
        maxWidth: '90%',
        minWidth: '50vw',
        maxHeight: '90vh',
        overflow: 'hidden',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
      }}
      content={
        <div className="p-2">
          <div className="w-100 mb-3">
            <span>
              {Number(conflictAmount || 0) > 0
                ? `Se registró un pago bancario de $${formatMoney(conflictAmount)} en este documento.
       ¿A qué nómina corresponde este pago?`
                : `Este pago ya fue conciliado con el banco. Indica a qué nómina
       corresponde o si es un flujo independiente.`}
            </span>
          </div>

          <div className="fs-16 documentDetails mt-4 mb-4">
            <span>
              {document?.documentType === 'TAX_DOCUMENT'
                ? 'Documento '
                : 'Obligación '}
              <span className="fw-bold">{document?.detail || ''}</span>
              <span className="fw-bold">
                {document?.folio ? ' #' + document.folio : ''}
              </span>
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              {Number(conflictAmount || 0) > 0 && (
                <span style={{ fontSize: 13 }}>
                  Monto conciliado:{' '}
                  <strong style={{ color: '#dc2626' }}>
                    $ {formatMoney(conflictAmount)}
                  </strong>
                </span>
              )}
              <span style={{ fontSize: 13 }}>
                Remanente:{' '}
                <strong>$ {formatMoney(document?.remaining)}</strong>
              </span>
            </div>
          </div>

          {Number(conflictAmount || 0) > 0 && (
            <div
              className="mb-3 p-2"
              style={{
                background: '#FFF7ED',
                border: '1px solid #FDBA74',
                borderRadius: 6,
              }}
            >
              <span style={{ fontWeight: 600 }}>Pago en conflicto: </span>
              <span style={{ fontWeight: 700, fontSize: 15 }}>
                $ {formatMoney(conflictAmount)}
              </span>
              <span style={{ color: '#6B7280', marginLeft: 8, fontSize: 13 }}>
                — Este monto fue conciliado bancariamente contra este documento.
                Indica a qué nómina corresponde o si es un flujo independiente.
              </span>
            </div>
          )}

          <Divider horizontal>Este pago corresponde a una nómina existente</Divider>
          <div className="mb-2">
            <Radio
              label="Vincular este pago a una nómina existente"
              name="imputationMode"
              value={NominaLinkMode.EXISTING_NOMINA}
              checked={mode === NominaLinkMode.EXISTING_NOMINA}
              disabled={isExistingDisabled}
              onChange={() => setMode(NominaLinkMode.EXISTING_NOMINA)}
            />
          </div>

          <div
            className="tableContainer"
            style={{
              opacity: mode === NominaLinkMode.EXISTING_NOMINA ? 1 : 0.5,
              pointerEvents:
                mode === NominaLinkMode.EXISTING_NOMINA ? 'auto' : 'none',
            }}
          >
            <div className="d-flex mr-3 nominaDocumentTitle">
              <div className="fw-bold w-5 text-center text-uppercase" />
              <div className="fw-bold w-30 text-center text-uppercase">
                nombre nómina
              </div>
              <div className="fw-bold w-20 text-center text-uppercase">estatus</div>
              <div className="fw-bold w-20 text-center text-uppercase">fecha</div>
              <div className="fw-bold w-20 text-center text-uppercase">
                monto pendiente
              </div>
            </div>
            <div className="tableScroll">
              {selections.length === 0 && (
                <div className="text-center p-3" style={{ color: '#888' }}>
                  No hay nóminas disponibles para vincular.
                </div>
              )}
              {selections.map((sel) => {
                const date =
                  sel?.dateApproveReject ||
                  sel?.dateToBank ||
                  sel?.dateInformed ||
                  sel?.dateToCreated ||
                  sel?.dateSelected
                return (
                  <div key={sel.id}>
                    <div className="d-flex align-items-center nominaDocumentRow custom-document-row mr-3 mb-3 d-flex mt-2">
                      <div className="d-flex text-center w-5 reconcileCheck cursor-pointer">
                        <Radio
                          name="selectionId"
                          checked={selectedSelectionId === sel.id}
                          onChange={() => setSelectedSelectionId(sel.id)}
                        />
                      </div>
                      <div className="w-30 text-center text-uppercase">
                        {sel?.nominaName || '-'}
                      </div>
                      <div className="w-20 text-center text-uppercase">
                        {statusLabels[sel?.status] || sel?.status || '-'}
                      </div>
                      <div className="w-20 text-center text-uppercase">
                        {date ? moment(date).format('DD MMM YYYY') : '-'}
                      </div>
                      <div className="w-20 text-center text-uppercase">
                        $ {formatMoney(getPendingNominaAmount(sel))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <Divider horizontal>o</Divider>
          <div className="d-flex align-items-center mr-3 mb-3 mt-3">
            <Radio
              label="Este pago no corresponde a ninguna nómina existente"
              name="imputationMode"
              value={NominaLinkMode.NEW_NOMINA}
              checked={mode === NominaLinkMode.NEW_NOMINA}
              onChange={() => {
                setMode(NominaLinkMode.NEW_NOMINA)
                setSelectedSelectionId(null)
              }}
            />
          </div>
        </div>
      }
    />
  )
}

export default ModalNominaImputation
